import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../context/SearchContext';
import '../styles/GlobalSearch.css';
import {
  FiSearch,
  FiUsers,
  FiUser,
  FiClock,
  FiFileText,
  FiActivity,
  FiFolder,
  FiX,
  FiArrowUp, // eslint-disable-line no-unused-vars
  FiArrowDown, // eslint-disable-line no-unused-vars
  FiCornerDownLeft,
  FiCommand, // eslint-disable-line no-unused-vars
  FiBook
} from 'react-icons/fi';

/**
 * Global Search Modal Component
 *
 * A comprehensive search interface that provides global search functionality across the application.
 * Features include real-time search across multiple data types, keyboard navigation, search filtering,
 * recent searches, and result highlighting. The modal supports searching through clients, users,
 * time entries, invoices, and activities with comprehensive keyboard shortcuts and navigation.
 *
 * Key Features:
 * - Universal search across all data types (clients, users, time entries, invoices, activities)
 * - Real-time search with debounced API calls via SearchContext
 * - Keyboard navigation with arrow keys, Enter, and Escape
 * - Category-based filtering with quick filter shortcuts
 * - Search result highlighting with query term emphasis
 * - Recent searches history with quick access
 * - Responsive design with proper focus management
 * - Auto-navigation to selected results
 *
 * Search Behavior:
 * - Integrates with SearchContext for state management and API calls
 * - Supports text highlighting in search results
 * - Maintains search history and recent searches
 * - Provides empty states for no results and initial load
 * - Auto-focuses search input when modal opens
 *
 * Keyboard Navigation:
 * - Arrow Up/Down: Navigate through search results
 * - Enter: Select highlighted result and navigate
 * - Escape: Close the search modal
 * - Tab: Navigate between filter options
 *
 * @returns {JSX.Element|null} The rendered global search modal or null if not open
 */
const GlobalSearchModal = () => {
  const navigate = useNavigate();
  const {
    isSearchOpen,
    searchQuery,
    searchResults,
    isLoading,
    recentSearches,
    searchType,
    closeSearch,
    setSearchQuery,
    setSearchType,
    handleResultSelect,
    getTotalResults,
    highlightText
  } = useSearch();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when modal opens - delay to allow CSS transition to complete
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      // Wait for the CSS transition (0.3s) to complete before focusing
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 350);

      return () => clearTimeout(timeoutId);
    }
  }, [isSearchOpen]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults, searchQuery]);

  // Get all results in a flat array for navigation
  const getAllResults = useCallback(() => {
    const results = [];

    if (searchType === 'all' || searchType === 'clients') {
      results.push(...(searchResults.clients || []));
    }
    if (searchType === 'all' || searchType === 'users') {
      results.push(...(searchResults.users || []));
    }
    if (searchType === 'all' || searchType === 'timeEntries') {
      results.push(...(searchResults.timeEntries || []));
    }
    if (searchType === 'all' || searchType === 'invoices') {
      results.push(...(searchResults.invoices || []));
    }
    if (searchType === 'all' || searchType === 'activities') {
      results.push(...(searchResults.activities || []));
    }
    if (searchType === 'all' || searchType === 'guide') {
      results.push(...(searchResults.guide || []));
    }

    return results;
  }, [searchResults, searchType]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const allResults = getAllResults();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % allResults.length);
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev === 0 ? allResults.length - 1 : prev - 1);
        break;

      case 'Enter':
        e.preventDefault();
        if (allResults[selectedIndex]) {
          handleResultClick(allResults[selectedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        closeSearch();
        break;

      default:
        break;
    }
  }, [getAllResults, selectedIndex, closeSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle result click
  const handleResultClick = useCallback((result) => {
    handleResultSelect(result);
    navigate(result.url);
  }, [handleResultSelect, navigate]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      closeSearch();
    }
  }, [closeSearch]);

  // Quick filter shortcuts
  const quickFilters = [
    { key: 'all', label: 'All', icon: FiSearch },
    { key: 'clients', label: 'Clients', icon: FiUsers },
    { key: 'users', label: 'Users', icon: FiUser },
    { key: 'timeEntries', label: 'Time', icon: FiClock },
    { key: 'invoices', label: 'Invoices', icon: FiFileText },
    { key: 'activities', label: 'Activities', icon: FiActivity },
    { key: 'guide', label: 'Guide', icon: FiBook }
  ];

  // Get icon component for result type
  const getResultIcon = (type) => {
    const iconMap = {
      client: FiUsers,
      user: FiUser,
      timeEntry: FiClock,
      invoice: FiFileText,
      activity: FiActivity,
      project: FiFolder,
      guide: FiBook
    };
    return iconMap[type] || FiSearch;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Render search category
  const renderCategory = (categoryKey, categoryLabel, categoryIcon, results) => {
    if (searchType !== 'all' && searchType !== categoryKey) return null;
    if (!results || results.length === 0) return null;

    const CategoryIcon = categoryIcon;

    return (
      <div className="search-category" key={categoryKey}>
        <div className="search-category-header">
          <CategoryIcon className="category-icon" />
          <span>{categoryLabel}</span>
          <span className="category-count">{results.length}</span>
        </div>
        
        {results.map((result, index) => {
          const globalIndex = getAllResults().findIndex(r => r.id === result.id && r.type === result.type);
          const ResultIcon = getResultIcon(result.type);
          
          return (
            <div
              key={`${result.type}-${result.id}`}
              className={`search-result-item ${globalIndex === selectedIndex ? 'highlighted' : ''}`}
              onClick={() => handleResultClick(result)}
            >
              <div className="search-result-icon">
                <ResultIcon />
              </div>
              
              <div className="search-result-content">
                <div 
                  className="search-result-title"
                  dangerouslySetInnerHTML={{ 
                    __html: highlightText(result.title, searchQuery) 
                  }}
                />
                <div 
                  className="search-result-subtitle"
                  dangerouslySetInnerHTML={{ 
                    __html: highlightText(result.subtitle, searchQuery) 
                  }}
                />
              </div>
              
              <div className="search-result-meta">
                {result.meta?.status && (
                  <span className="search-result-badge">
                    {result.meta.status}
                  </span>
                )}
                {result.meta?.lastActivity && (
                  <span className="search-result-time">
                    {formatDate(result.meta.lastActivity)}
                  </span>
                )}
                {result.meta?.date && (
                  <span className="search-result-time">
                    {formatDate(result.meta.date)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    if (isLoading) {
      return (
        <div className="search-loading">
          <div className="search-spinner" />
        </div>
      );
    }

    if (!searchQuery) {
      return (
        <div className="search-empty-state">
          <FiSearch className="search-empty-icon" />
          <div className="search-empty-title">Start typing to search</div>
          <div className="search-empty-subtitle">
            Search across clients, users, time entries, invoices, user guide, and more
          </div>
          
          {recentSearches.length > 0 && (
            <div className="recent-searches">
              <h4>Recent Searches</h4>
              {recentSearches.slice(0, 10).map(search => (
                <div 
                  key={search.id}
                  className="search-result-item"
                  onClick={() => setSearchQuery(search.query)}
                >
                  <div className="search-result-icon">
                    <FiSearch />
                  </div>
                  <div className="search-result-content">
                    <div className="search-result-title">{search.query}</div>
                    <div className="search-result-subtitle">
                      {search.type} • {formatDate(search.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (getTotalResults() === 0) {
      return (
        <div className="search-empty-state">
          <FiSearch className="search-empty-icon" />
          <div className="search-empty-title">No results found</div>
          <div className="search-empty-subtitle">
            Try adjusting your search terms or browse by category
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={`global-search-overlay ${isSearchOpen ? 'open' : ''}`}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      style={{ pointerEvents: isSearchOpen ? 'auto' : 'none' }}
    >
      <div className="global-search-modal">
        {/* Header */}
        <div className="global-search-header">
          <div className="global-search-input-container">
            <FiSearch className="global-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="global-search-input"
              placeholder="Search everything..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          
          {/* Quick filter shortcuts */}
          <div className="global-search-shortcuts">
            {quickFilters.map(filter => {
              const FilterIcon = filter.icon;
              return (
                <button
                  key={filter.key}
                  className={`search-shortcut ${searchType === filter.key ? 'active' : ''}`}
                  onClick={() => {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('Filter clicked:', filter.key);
                    }
                    setSearchType(filter.key);
                  }}
                >
                  <FilterIcon className="shortcut-icon" />
                  <span>{filter.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        <div className="global-search-results" ref={resultsRef}>
          {renderEmptyState()}
          
          {getTotalResults() > 0 && (
            <>
              {renderCategory('clients', 'Clients', FiUsers, searchResults.clients)}
              {renderCategory('users', 'Team Members', FiUser, searchResults.users)}
              {renderCategory('timeEntries', 'Time Entries', FiClock, searchResults.timeEntries)}
              {renderCategory('invoices', 'Invoices', FiFileText, searchResults.invoices)}
              {renderCategory('activities', 'Activities', FiActivity, searchResults.activities)}
              {renderCategory('guide', 'User Guide', FiBook, searchResults.guide)}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="global-search-footer">
          <div className="search-footer-shortcuts">
            <div className="search-footer-shortcut">
              <span className="shortcut-key">↑↓</span>
              <span>Navigate</span>
            </div>
            <div className="search-footer-shortcut">
              <FiCornerDownLeft size={12} />
              <span>Select</span>
            </div>
            <div className="search-footer-shortcut">
              <span className="shortcut-key">Esc</span>
              <span>Close</span>
            </div>
          </div>
          
          <button className="search-close-button" onClick={closeSearch}>
            <FiX />
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;